import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import { useInviteMember } from "../../../application/hooks/useGroups";
import type { GroupRole } from "@tabipla/shared";

interface InviteMemberDialogProps {
  groupId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberDialog({ groupId, open, onOpenChange }: InviteMemberDialogProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Exclude<GroupRole, "owner">>("editor");

  const inviteMutation = useInviteMember(groupId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      await inviteMutation.mutateAsync({ email, role });
      toast.success(t("groups.inviteSuccess", "招待を送信しました"));
      onOpenChange(false);
      setEmail("");
      setRole("editor");
    } catch {
      toast.error(t("groups.inviteError", "招待に失敗しました"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-600" />
            {t("groups.inviteTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("groups.inviteDescription", "メールアドレスを入力してメンバーを招待します")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("groups.email")}</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("groups.emailPlaceholder")}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("groups.role")}</label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as Exclude<GroupRole, "owner">)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">{t("groups.roles.editor")}</SelectItem>
                  <SelectItem value="viewer">{t("groups.roles.viewer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={inviteMutation.isPending || !email.trim()}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 sm:flex-none"
            >
              {inviteMutation.isPending ? t("groups.inviting") : t("groups.invite")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
